/**
 * Roadmap-diagram — berekend render-pad (layout_mode = "roadmap",
 * roadmap-tijdreizen.md §6.1). Toont de plateau-keten als horizontale lanes
 * (Plateau 1 = main = "nu" links, toekomst-plateaus naar rechts). Onderaan een
 * tijd-slider = scrub = de actieve branch kiezen (slider onder Plateau 2 →
 * branch van Plateau 2 actief). De "+" maakt een nieuw Plateau-object +
 * gechainde branch (POST /branches/plateau).
 *
 * De plaatsing van I&M-elementen (Gaps / Work Packages / Deliverables / Events)
 * ín de lanes volgt in een vervolg-stap; deze eerste versie levert de
 * navigatie-structuur (lanes + tijdreizen + plateau-aanmaak).
 *
 * Branch-switch hergebruikt exact het BranchPicker-pad: `setBranchId` +
 * `useWorkspaceData.hydrate()`. Omdat het Roadmap-diagram branch-transparant is
 * (Stap 3a) blijft 't open terwijl de rest van de app de gekozen plateau-versie
 * van de wereld toont.
 */

import type { BoxStyle, Concept, Diagram } from "@ae/domain";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ApiError, api, type PlateauBranch } from "../../api/client";
import { getRegistry, resolveBilingual } from "../../registry";
import { MergeBranchDialog } from "../../settings/MergeBranchDialog";
import { useDisplayMode } from "../../state/displayMode";
import { useSession } from "../../state/session";
import { useUi } from "../../state/ui";
import { useWorkspaceData } from "../../state/workspace";
import { AiBadge } from "../AiBadge";
import { confirmHardDelete } from "../cascadeConfirm";
import { ImportingModal } from "../ImportingModal";
import { colorForConcept, type LayerColor } from "../layerColors";
import { readDroppedTypeName } from "../palette/dragTypeName";
import { BoxFormatToolbar } from "./BoxFormatToolbar";
import { boxFillCss, boxTextCss, readBoxStyles } from "./diagramBoxStyles";
import { getElementIcon } from "./elementIcons";
import { roadmapLayout } from "./roadmapLayout";
import { ZoomPan } from "./ZoomPan";

// Roadmap-lane-model (gebruiker-verduidelijking 2026-06-20):
//  - Werkpakket in een plateau-lane → Realisatie WP→Plateau (membership) +
//    Realisatie WP→Program (de roadmap). Links uitgelijnd kaartje.
//  - Deliverable ge-dropt OP een Werkpakket → Realisatie WP→Deliverable +
//    Realisatie Deliverable→Program. Rechts uitgelijnd kaartje onder de WP.
//  - Overal dubbele-relatie-preventie (check vóór aanmaak).
const WP_TYPE = "archimate.WorkPackage";
const DELIVERABLE_TYPE = "archimate.Deliverable";
const PROGRAM_TYPE = "archimate.Program";
const PLATEAU_TYPE = "archimate.Plateau";
const REL_TYPE = "archimate.Realization";
const EXISTING_CONCEPT_MIME = "application/ae-existing-concept-id";
// Multi-select drag vanuit Explorer/ConceptExplorer: JSON-array van concept-ids.
const EXISTING_CONCEPT_IDS_MIME = "application/ae-existing-concept-ids";
const TYPE_ID_MIME = "application/ae-type-id";
// Sorteer-rang van een Werkpakket binnen z'n plateau-lane (infra B). Leeft als
// los attribuut op het WP-concept zodat 't met de WP meereist en branch-
// transparant op WP-main staat. Geen rang → onderaan (Infinity, stabiele sort
// houdt de relatie-volgorde aan voor legacy-WP's zonder rang).
const ORDER_ATTR = "roadmap_order";
function wpOrder(wp: Concept): number {
  const v = (wp.attributes as Record<string, unknown> | undefined)?.[ORDER_ATTR];
  return typeof v === "number" ? v : Number.POSITIVE_INFINITY;
}

/** Parse de bulk-drag-payload (JSON-array van concept-ids) defensief. */
function parseBulkIds(raw: string): string[] {
  try {
    const p = JSON.parse(raw);
    return Array.isArray(p) ? p.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}

/** Inline laad-spinner (zelfde stijl als ImportingModal). */
function Spinner() {
  return (
    <svg
      className="h-6 w-6 animate-spin text-blue-600"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.2" strokeWidth="3" />
      <path
        d="M22 12a10 10 0 0 1-10 10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

/** Canvas-achtig kaartje voor een I&M-object in een plateau-lane: afgeronde
 *  rechthoek met type-icoon + naam, in de object-kleur (zoals op het canvas).
 *  WP = links uitgelijnd + accepteert Deliverable-drops; Deliverable = rechts. */
function ObjectCard({
  concept,
  align,
  lang,
  t,
  isSelected,
  onSelect,
  onContextMenu,
  onDropDeliverable,
  boxStyle,
}: {
  concept: Concept;
  align: "left" | "right";
  lang: "nl" | "en";
  t: (k: string) => string;
  isSelected: boolean;
  onSelect: () => void;
  onContextMenu?: (e: React.MouseEvent) => void;
  onDropDeliverable?: (e: React.DragEvent) => void;
  boxStyle?: BoxStyle | undefined;
}) {
  const reg = getRegistry();
  const et = reg.getElementType(concept.typeId);
  const displayMode = useDisplayMode((s) => s.mode);
  const color = colorForConcept({
    typeId: concept.typeId,
    layer: et?.layer ?? "implementation_migration",
    attributes: concept.attributes,
    stereotypes: concept.stereotypes,
    displayMode,
  });
  const name = resolveBilingual(concept.name, lang) || t("roadmap.unnamed");
  // Alleen kijken: aanklikken mag, verslepen niet.
  const readOnly = useSession((s) => s.readOnly);
  return (
    <button
      type="button"
      title={name}
      draggable={!readOnly}
      onDragStart={(e) => {
        // Versleep dit object — als bestaand concept, zodat de drop-handlers
        // (andere WP, lane) 't oppikken (verplaatsen/kopiëren).
        e.dataTransfer.setData(EXISTING_CONCEPT_MIME, concept.id);
        // copyMove (niet "move") — de drop-targets zetten dropEffect="copy";
        // bij effectAllowed="move" weigert HTML5-DnD die combinatie en vuurt
        // onDrop niet → geen verplaats/kopieer-vraag.
        e.dataTransfer.effectAllowed = "copyMove";
      }}
      onContextMenu={onContextMenu}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
      className={`relative flex max-w-[88%] flex-col items-center justify-center rounded-lg border px-3 py-2 text-center text-xs shadow-sm ${
        align === "right" ? "ml-auto" : ""
      } ${onDropDeliverable ? "cursor-copy" : ""} ${isSelected ? "ring-2 ring-blue-500" : ""}`}
      style={{
        backgroundColor: color.bg,
        borderColor: color.border,
        color: color.text,
        ...boxFillCss(boxStyle),
        ...boxTextCss(boxStyle),
      }}
      onDragOver={
        onDropDeliverable
          ? (e) => {
              e.preventDefault();
              e.stopPropagation();
              e.dataTransfer.dropEffect = "copy";
            }
          : undefined
      }
      onDrop={onDropDeliverable}
    >
      {/* Type-icoon rechtsboven, zoals op het normale canvas. */}
      <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center opacity-80">
        {getElementIcon(concept.typeId)}
      </span>
      <span className="break-words font-medium">{name}</span>
    </button>
  );
}

interface RoadmapCanvasProps {
  diagram: Diagram;
}

export function RoadmapCanvas({ diagram }: RoadmapCanvasProps) {
  const { t, i18n } = useTranslation();
  const lang: "nl" | "en" = i18n.language === "en" ? "en" : "nl";
  const workspaceId = useSession((s) => s.workspaceId);
  const branchId = useSession((s) => s.branchId);
  const setBranchId = useSession((s) => s.setBranchId);
  // Alleen kijken: lanes aanklikken (branch wisselen) en selecteren mag; geen
  // plateau erbij, geen drops, geen opmaak, wissen of samenvoegen.
  const readOnly = useSession((s) => s.readOnly);
  // Re-render wanneer de workspace-store muteert (na een drop-create + hydrate).
  const revision = useWorkspaceData((s) => s.revision);
  const displayMode = useDisplayMode((s) => s.mode);
  // Per-box opmaak (font/kleur/uitlijning). Box-key = `roadmap:<conceptId>`
  // (per concept, consistent over de plateau-lanes heen).
  const [selectedBoxKey, setSelectedBoxKey] = useState<string | null>(null);
  const boxStyles = useMemo(() => {
    void revision;
    return readBoxStyles(diagram.viewState);
  }, [diagram.viewState, revision]);
  const patchBox = useCallback(
    (key: string, patch: BoxStyle) => {
      if (!workspaceId || !branchId) return;
      if (useSession.getState().readOnly) return;
      useWorkspaceData.getState().setDiagramBoxStyle(workspaceId, branchId, diagram.id, key, patch);
    },
    [workspaceId, branchId, diagram.id],
  );
  const selectedConceptId = useUi((s) => s.selectedConceptId);

  // Visuele selectie op KAARTJE-niveau (niet concept-niveau): hetzelfde concept
  // kan onder meerdere parents als apart kaartje verschijnen — alleen het
  // aangeklikte mag de ring krijgen. Het AttributePanel volgt wél het concept.
  // Bevat naast de kaartje-key ook de delete-context (concept/isWp/parent) zodat
  // de Del/Cmd+Del-toetsen exact hetzelfde ene kaartje weghalen als 't contextmenu.
  const [selectedCard, setSelectedCard] = useState<{
    conceptId: string;
    cardKey: string;
    isWp: boolean;
    parentId: string | null;
  } | null>(null);

  // Selecteer een kaartje → highlight (per kaartje) + open het AttributePanel
  // (op de roadmap zelf gemount, onderaan deze component).
  const selectCard = (
    conceptId: string,
    cardKey: string,
    isWp: boolean,
    parentId: string | null,
  ) => {
    setSelectedCard({ conceptId, cardKey, isWp, parentId });
    useUi.getState().selectConcept(conceptId);
    useUi.getState().setAttributePanelOpen(true);
  };

  const [lanes, setLanes] = useState<PlateauBranch[]>([]);
  const [dragOverPlateau, setDragOverPlateau] = useState<string | null>(null);
  // Tijd laten voortschrijden: een plateau opgaan in het vorige (branch-merge.md §3.11).
  const [mergeLane, setMergeLane] = useState<PlateauBranch | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);
  // Hangende verplaats/kopieer-keuze: deliverable `delId` op WP `wpId`, met de
  // relatie-ids van de huidige WP-eigenaar(s) (`ownerRelIds`) voor "verplaatsen".
  const [pendingMove, setPendingMove] = useState<{
    wpId: string;
    delId: string;
    ownerRelIds: string[];
    laneBranchId: string;
  } | null>(null);
  // In-app melding (i.p.v. window.alert) voor validatie-fouten bij een drop.
  const [notice, setNotice] = useState<string | null>(null);
  // Rechtermuis-contextmenu op een WP-/Deliverable-kaartje (verwijderen).
  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    conceptId: string;
    isWp: boolean;
    // Parent-context van dít kaartje: voor een WP = plateau-concept-id van de
    // lane; voor een deliverable = het WP waaronder 't hangt. Bepaalt welke
    // ene membership-relatie "Verwijderen" weghaalt.
    parentId: string | null;
  } | null>(null);
  // Eenmalige auto-switch-naar-main per keer dat de roadmap geopend wordt
  // (mount). Voorkomt dat 'ie je terugtrekt naar main als je later handmatig
  // van branch wisselt terwijl de roadmap open staat.
  const autoSwitched = useRef(false);

  const refresh = useCallback(async () => {
    if (!workspaceId) return;
    // Actieve branch bepaalt de WP (per-WP roadmap). Via getState zodat een
    // branch-switch niet onnodig her-fetcht — binnen één WP blijven de lanes
    // gelijk.
    const current = useSession.getState().branchId;
    if (!current) return;
    setState("loading");
    try {
      const res = await api.branchPlateaus(workspaceId, current);
      setLanes(res);
      setState("ready");
      // Bij openen: zit je niet op een plateau (bv. een feature-branch), land
      // dan op Plateau 1 = main = "huidige situatie". Eenmalig per mount.
      if (!autoSwitched.current) {
        autoSwitched.current = true;
        const current = useSession.getState().branchId;
        const onPlateau = res.some((l) => l.id === current);
        if (!onPlateau) {
          const main = res.find((l) => l.isMain) ?? res[0];
          if (main && main.id !== current) {
            setBranchId(main.id);
            await useWorkspaceData.getState().hydrate(workspaceId, main.id);
          }
        }
      }
    } catch {
      setState("error");
    }
  }, [workspaceId, setBranchId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeIndex = useMemo(() => lanes.findIndex((l) => l.id === branchId), [lanes, branchId]);

  // De roadmap als boom: kolom = tijdstap, rij = toekomstpad, zusters in
  // volgorde van aanmaken (roadmapLayout.ts).
  const layout = useMemo(() => roadmapLayout(lanes), [lanes]);
  /** Per kolom de plateaus van boven naar beneden, voor de tijdschuif. */
  const columns = useMemo(() => {
    const cols: Array<Array<{ lane: PlateauBranch; idx: number }>> = [];
    for (const cell of [...layout.cells].sort((a, b) => a.row - b.row)) {
      if (!cols[cell.col]) cols[cell.col] = [];
      cols[cell.col]?.push({ lane: cell.lane, idx: cell.idx });
    }
    return cols.filter((c) => c && c.length > 0);
  }, [layout]);

  // Plateau 1 = de canonieke main = "de huidige situatie". Toon dat label i.p.v.
  // de rauwe branch-naam (bv. "main-68c0e14f").
  const laneLabel = useCallback(
    (lane: PlateauBranch) => {
      // Toon de naam van het gekoppelde Plateau-concept (niet de branch-naam).
      if (lane.plateauConceptId) {
        const c = useWorkspaceData.getState().store.getConcept(lane.plateauConceptId);
        const nm = c ? resolveBilingual(c.name, lang) : "";
        if (nm) return nm;
      }
      return lane.isMain ? t("roadmap.currentSituation") : lane.name;
    },
    [t, lang],
  );

  const switchTo = useCallback(
    async (targetBranchId: string) => {
      if (!workspaceId || targetBranchId === branchId) return;
      setBranchId(targetBranchId);
      await useWorkspaceData.getState().hydrate(workspaceId, targetBranchId);
    },
    [workspaceId, branchId, setBranchId],
  );

  // Na een drop in een lane: ververs de store én activeer de branch van die
  // lane (gebruiker-wens — slepen in een plateau = dat plateau actief maken).
  // Zit je al op die branch, dan alleen verversen.
  const refreshAndActivate = useCallback(
    async (laneBranchId: string) => {
      if (!workspaceId) return;
      if (laneBranchId !== useSession.getState().branchId) {
        await switchTo(laneBranchId);
      } else {
        await useWorkspaceData.getState().hydrate(workspaceId, laneBranchId);
      }
    },
    [workspaceId, switchTo],
  );

  const addPlateau = useCallback(async () => {
    const name = newName.trim();
    if (!workspaceId || !name || busy) return;
    if (useSession.getState().readOnly) return;
    setBusy(true);
    try {
      // Parent = laatste plateau in de keten (= het meest recente plateau).
      const parent = lanes.length > 0 ? lanes[lanes.length - 1] : undefined;
      // Auto-vertaal de plateau-naam zodat het Plateau-concept bilingual is
      // (de getypte naam in z'n eigen taalveld, de vertaling in het andere).
      let nameNl = name;
      let nameEn: string | undefined;
      try {
        const tr = await api.translateAuto(name, "name");
        if (tr.source_lang === "nl") {
          nameNl = name;
          nameEn = tr.text;
        } else {
          nameNl = tr.text;
          nameEn = name;
        }
      } catch {
        // Geen vertaling beschikbaar → alleen de getypte naam (nl-fallback).
      }
      const res = await api.createPlateau(workspaceId, {
        name,
        nameNl,
        ...(nameEn ? { nameEn } : {}),
        ...(parent ? { parent_branch_id: parent.id } : {}),
      });
      setNewName("");
      setAdding(false);
      await refresh();
      if (res.branch?.id) await switchTo(res.branch.id);
    } catch (err) {
      // Naam-conflict (409) e.d. — toon kort, laat de invoer staan.
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }, [workspaceId, newName, busy, lanes, refresh, switchTo]);

  // Per lane: de Werkpakketten die dit plateau realiseren (WP→Plateau), en per
  // WP z'n Deliverables (WP→Deliverable). Afgeleid uit de Realisatie-relaties in
  // de store; I&M-objecten leven branch-transparant op WP-main. `revision`
  // triggert her-berekening. Keyed op lane.id.
  const structureByPlateau = useMemo(() => {
    const store = useWorkspaceData.getState().store;
    void revision;
    const rels = store.listRelations().filter((r) => !r.tombstoneInBranch && r.typeId === REL_TYPE);
    const map = new Map<string, { wp: Concept; deliverables: Concept[] }[]>();
    for (const lane of lanes) {
      const pid = lane.plateauConceptId;
      if (!pid) {
        map.set(lane.id, []);
        continue;
      }
      const wps: { wp: Concept; deliverables: Concept[] }[] = [];
      const seenWp = new Set<string>();
      for (const r of rels) {
        if (r.targetConceptId !== pid) continue;
        const wp = store.getConcept(r.sourceConceptId);
        if (!wp || wp.typeId !== WP_TYPE || seenWp.has(wp.id)) continue;
        seenWp.add(wp.id);
        const dels: Concept[] = [];
        const seenD = new Set<string>();
        for (const r2 of rels) {
          if (r2.sourceConceptId !== wp.id) continue;
          const d = store.getConcept(r2.targetConceptId);
          if (!d || d.typeId !== DELIVERABLE_TYPE || seenD.has(d.id)) continue;
          seenD.add(d.id);
          dels.push(d);
        }
        wps.push({ wp, deliverables: dels });
      }
      // Sorteer op roadmap_order (infra B). Stabiel: WP's zonder rang (Infinity)
      // behouden hun relatie-volgorde onderaan.
      wps.sort((a, b) => wpOrder(a.wp) - wpOrder(b.wp));
      map.set(lane.id, wps);
    }
    return map;
  }, [lanes, revision]);

  // Lane-kleur = de kleur van het gekoppelde Plateau-object (layer-/stereotype-/
  // attribuut-kleur via colorForConcept). Geen plateau-object → null (default-
  // grijs). `revision` triggert her-berekening na een kleur-wijziging.
  const laneColors = useMemo(() => {
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    void revision;
    const map = new Map<string, LayerColor | null>();
    for (const lane of lanes) {
      const c = lane.plateauConceptId ? store.getConcept(lane.plateauConceptId) : null;
      if (!c) {
        // Geen plateau-object (bv. Plateau 1 = main) → toch kleuren met de
        // default Plateau-kleur, zodat ook de "huidige situatie"-lane meedoet.
        const et = reg.getElementType("archimate.Plateau");
        map.set(
          lane.id,
          colorForConcept({
            typeId: "archimate.Plateau",
            layer: et?.layer ?? "implementation_migration",
          }),
        );
        continue;
      }
      const et = reg.getElementType(c.typeId);
      map.set(
        lane.id,
        colorForConcept({
          typeId: c.typeId,
          layer: et?.layer ?? "implementation_migration",
          attributes: c.attributes,
          stereotypes: c.stereotypes,
          displayMode,
        }),
      );
    }
    return map;
  }, [lanes, revision, displayMode]);

  // Gap- en Differences(plateau-diff)-diagram-id's voor de zij-knoppen.
  const siblingDiagrams = useMemo(() => {
    const store = useWorkspaceData.getState().store;
    const reg = getRegistry();
    void revision;
    let gap: string | null = null;
    let diff: string | null = null;
    for (const d of store.listDiagrams()) {
      const lm = reg.getViewKind(d.viewKind)?.layout_mode;
      if (lm === "gap-diff") gap = d.id;
      else if (lm === "plateau-diff") diff = d.id;
    }
    return { gap, diff };
  }, [revision]);

  const openSibling = (id: string | null) => {
    if (id && workspaceId) useWorkspaceData.getState().openDiagram(workspaceId, id);
  };

  // ── Drop-logica + relatie-helpers (dubbel-preventie) ───────────────────
  const programIdRef = useRef<string | null>(null);

  const hasRealization = (src: string, tgt: string): boolean =>
    useWorkspaceData
      .getState()
      .store.listRelations()
      .some(
        (r) =>
          !r.tombstoneInBranch &&
          r.typeId === REL_TYPE &&
          r.sourceConceptId === src &&
          r.targetConceptId === tgt,
      );

  // Maak een Realisatie src→tgt, maar alleen als 'ie nog niet bestaat (dup-preventie).
  const ensureRealization = async (src: string, tgt: string): Promise<void> => {
    if (!workspaceId || !branchId || hasRealization(src, tgt)) return;
    await api.createRelation(workspaceId, branchId, {
      type_id: REL_TYPE,
      source_concept_id: src,
      target_concept_id: tgt,
    });
  };

  // Program (= de roadmap) ensuren + binden aan 't roadmap-diagram (attributes).
  const ensureProgram = async (): Promise<string | null> => {
    if (!workspaceId || !branchId) return null;
    if (programIdRef.current) return programIdRef.current;
    const store = useWorkspaceData.getState().store;
    const attrs = diagram.attributes as Record<string, unknown> | undefined;
    const bound = attrs?.program_concept_id;
    if (typeof bound === "string" && store.getConcept(bound)) {
      programIdRef.current = bound;
      return bound;
    }
    const created = await api.createConcept(workspaceId, branchId, {
      type_id: PROGRAM_TYPE,
      name: diagram.name ?? { nl: "Roadmap" },
    });
    const pid = (created as { id: string }).id;
    await api.patchDiagram(workspaceId, branchId, diagram.id, {
      attributes: { ...(attrs ?? {}), program_concept_id: pid },
    });
    programIdRef.current = pid;
    return pid;
  };

  // Plateau-concept van een lane; main heeft er geen → ensure via endpoint.
  const ensurePlateauForLane = async (lane: PlateauBranch): Promise<string | null> => {
    if (lane.plateauConceptId) return lane.plateauConceptId;
    if (!workspaceId) return null;
    const res = await api.ensurePlateauConcept(workspaceId, lane.id, {
      name: t("roadmap.currentSituation"),
    });
    return res.plateauConceptId;
  };

  // Drop op een plateau-lane → Werkpakket: WP→Plateau (membership) + WP→Program.
  // Persisteer de rang (roadmap_order) van een geordende lijst WP-ids (infra B,
  // #4). Patcht alleen de WP's waarvan de rang wijzigt — volledige gemergede
  // attributes meegestuurd zodat 't werkt of de worker nu merge of replace doet.
  const persistWpOrder = async (orderedWpIds: string[]): Promise<void> => {
    if (!workspaceId || !branchId) return;
    const store = useWorkspaceData.getState().store;
    for (let i = 0; i < orderedWpIds.length; i++) {
      const wp = store.getConcept(orderedWpIds[i] as string);
      if (!wp || wpOrder(wp) === i) continue;
      const attrs = {
        ...((wp.attributes as Record<string, unknown> | undefined) ?? {}),
        [ORDER_ATTR]: i,
      };
      await api.patchConcept(workspaceId, branchId, wp.id, { attributes: attrs });
    }
  };

  // Verwijder ÉÉN kaartje uit de roadmap.
  //  - hard=false (Verwijderen): alleen de membership-relatie met DIT specifieke
  //    parent-object weg (WP→ditPlateau, resp. ditWP→Deliverable). Hetzelfde
  //    concept kan onder meerdere parents hangen — alleen het aangeklikte kaartje
  //    verdwijnt. De Program-link gaat pas weg als 't de laatste membership was.
  //  - hard=true (Definitief): removeConcept → concept getombstoned (Prullenbak),
  //    z'n relaties cascaden mee. Onderliggende deliverables blijven als concept.
  const performDelete = async (
    conceptId: string,
    isWp: boolean,
    hard: boolean,
    parentId: string | null,
  ): Promise<void> => {
    setCtxMenu(null);
    if (!workspaceId || !branchId || busy) return;
    if (useSession.getState().readOnly) return;
    // Definitief verwijderen → dezelfde cascade-bewuste bevestiging als elders
    // (Canvas Cmd+Del / Explorer). Vóór setBusy zodat de spinner niet over de
    // confirm heen valt. "Verwijderen" (membership-relatie) is triviaal
    // ongedaan te maken, dus geen bevestiging.
    if (hard) {
      const ok = await confirmHardDelete({
        workspaceId,
        branchId,
        conceptIds: [conceptId],
        lang,
        standaloneEdgeCount: 0,
        conceptName: (id) => useWorkspaceData.getState().store.getConcept(id)?.name,
      });
      if (!ok) return;
    }
    setBusy(true);
    try {
      if (hard) {
        await useWorkspaceData.getState().removeConcept(workspaceId, branchId, conceptId);
      } else {
        const store = useWorkspaceData.getState().store;
        const rels = store
          .listRelations()
          .filter((r) => !r.tombstoneInBranch && r.typeId === REL_TYPE);
        const ids = new Set<string>();
        if (isWp) {
          // Alle WP→Plateau-memberships van deze WP; verwijder alleen die van
          // het aangeklikte plateau (parentId). Geen parentId → alle (fallback).
          const plateauLinks = rels.filter(
            (r) =>
              r.sourceConceptId === conceptId &&
              store.getConcept(r.targetConceptId)?.typeId === PLATEAU_TYPE,
          );
          for (const r of plateauLinks) {
            if (!parentId || r.targetConceptId === parentId) ids.add(r.id);
          }
          // Program-link alleen weg als er na deze verwijdering geen plateau-
          // membership meer overblijft.
          if (plateauLinks.every((r) => ids.has(r.id))) {
            for (const r of rels) {
              if (
                r.sourceConceptId === conceptId &&
                store.getConcept(r.targetConceptId)?.typeId === PROGRAM_TYPE
              ) {
                ids.add(r.id);
              }
            }
          }
        } else {
          // Alle WP→Deliverable-links naar deze deliverable; verwijder alleen die
          // van het aangeklikte werkpakket (parentId). Geen parentId → alle.
          const wpLinks = rels.filter(
            (r) =>
              r.targetConceptId === conceptId &&
              store.getConcept(r.sourceConceptId)?.typeId === WP_TYPE,
          );
          for (const r of wpLinks) {
            if (!parentId || r.sourceConceptId === parentId) ids.add(r.id);
          }
          // Deliverable→Program alleen weg als er geen WP-link meer overblijft.
          if (wpLinks.every((r) => ids.has(r.id))) {
            for (const r of rels) {
              if (
                r.sourceConceptId === conceptId &&
                store.getConcept(r.targetConceptId)?.typeId === PROGRAM_TYPE
              ) {
                ids.add(r.id);
              }
            }
          }
        }
        for (const id of ids) await api.deleteRelation(workspaceId, branchId, id);
      }
      if (useUi.getState().selectedConceptId === conceptId) {
        useUi.getState().selectConcept(null);
      }
      setSelectedCard(null);
      await useWorkspaceData.getState().hydrate(workspaceId, branchId);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Del/Cmd+Del op een geselecteerd kaartje → verwijderen / definitief
  // verwijderen, exact dezelfde semantiek als 't contextmenu. Via een ref zodat
  // de listener één keer geregistreerd wordt maar altijd de laatste state +
  // performDelete leest (geen stale closure, geen her-subscribe per render).
  const keyDeleteRef = useRef<{
    selected: typeof selectedCard;
    run: typeof performDelete;
    busy: boolean;
  }>({ selected: null, run: performDelete, busy });
  keyDeleteRef.current = { selected: selectedCard, run: performDelete, busy };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const st = keyDeleteRef.current;
      if (!st.selected || st.busy) return;
      // Niet kapen wanneer focus in een tekstveld/dropdown/contenteditable zit
      // (anders breekt Del/Backspace in inputs én in het AttributePanel).
      const tgt = e.target;
      if (tgt instanceof HTMLElement) {
        const tag = tgt.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || tgt.isContentEditable) {
          return;
        }
      }
      e.preventDefault();
      const hard = e.metaKey || e.ctrlKey;
      void st.run(st.selected.conceptId, st.selected.isWp, hard, st.selected.parentId);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const onDropOnLane = async (lane: PlateauBranch, e: React.DragEvent): Promise<void> => {
    e.preventDefault();
    setDragOverPlateau(null);
    if (!workspaceId || !branchId || busy) return;
    if (useSession.getState().readOnly) return;
    // Activeer de plateau-branch van deze lane bij ÉLKE drop erin — ook als de
    // drop verder niets oplevert (bv. ongeldig type of al aanwezig).
    void switchTo(lane.id);
    // dataTransfer SYNCHROON lezen — na een await is 't drop-event weg.
    const bulkRaw = e.dataTransfer.getData(EXISTING_CONCEPT_IDS_MIME) || null;
    const existingId = e.dataTransfer.getData(EXISTING_CONCEPT_MIME) || null;
    const typeId = e.dataTransfer.getData(TYPE_ID_MIME) || null;
    const droppedName = readDroppedTypeName(e.dataTransfer);
    const store = useWorkspaceData.getState().store;
    // Multi-select drag → elk Werkpakket in de selectie als membership toevoegen.
    if (bulkRaw) {
      const ids = parseBulkIds(bulkRaw);
      const wpIds = ids.filter((id) => store.getConcept(id)?.typeId === WP_TYPE);
      if (wpIds.length === 0) {
        setNotice(t("roadmap.dropWpOnLane"));
        return;
      }
      setBusy(true);
      try {
        const plateauId = await ensurePlateauForLane(lane);
        if (!plateauId) throw new Error("no plateau");
        const programId = await ensureProgram();
        for (const wpId of wpIds) {
          await ensureRealization(wpId, plateauId);
          if (programId) await ensureRealization(wpId, programId);
        }
        await refreshAndActivate(lane.id);
      } catch (err) {
        window.alert(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    const draggedType =
      typeId ?? (existingId ? (store.getConcept(existingId)?.typeId ?? null) : null);
    if (draggedType !== WP_TYPE) {
      if (!existingId) setNotice(t("roadmap.dropWpOnLane"));
      return;
    }
    // Huidige WP-volgorde in deze lane (display-order).
    const existingOrderedIds = (structureByPlateau.get(lane.id) ?? []).map((x) => x.wp.id);
    // #4 — bestaande WP die al in deze lane zit, gedropt op de lege lane-ruimte
    // → naar ONDERAAN verplaatsen (drop op een ander WP-kaartje = invoegen vóór
    // dat kaartje, via onDropOnWp). Deliverables hangen genest mee.
    if (existingId && existingOrderedIds.includes(existingId)) {
      const reordered = existingOrderedIds.filter((id) => id !== existingId);
      reordered.push(existingId);
      setBusy(true);
      try {
        await persistWpOrder(reordered);
        await refreshAndActivate(lane.id);
      } catch (err) {
        window.alert(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    setBusy(true);
    try {
      const plateauId = await ensurePlateauForLane(lane);
      if (!plateauId) throw new Error("no plateau");
      const programId = await ensureProgram();
      let wpId = existingId;
      let createdAttrs: Record<string, unknown> = {};
      const isNewWp = !existingId && !!typeId;
      if (isNewWp && typeId) {
        const created = (await api.createConcept(workspaceId, branchId, {
          type_id: typeId,
          ...(droppedName ? { name: droppedName } : {}),
        })) as { id: string; attributes?: Record<string, unknown> };
        wpId = created.id;
        createdAttrs = created.attributes ?? {};
      }
      if (!wpId) return;
      await ensureRealization(wpId, plateauId);
      if (programId) await ensureRealization(wpId, programId);
      // #2 — nieuwe WP onderaan in de lane + meteen z'n AttributePanel openen.
      // Eerst de bestaande WP's concrete rangen (0..n-1) geven, dan de nieuwe de
      // hoogste rang. Volledige attributes meegestuurd (merge) zodat een
      // replace-semantiek op de worker de defaults niet wist.
      if (isNewWp) {
        for (let i = 0; i < existingOrderedIds.length; i++) {
          const ex = store.getConcept(existingOrderedIds[i] as string);
          if (!ex || wpOrder(ex) === i) continue;
          await api.patchConcept(workspaceId, branchId, ex.id, {
            attributes: {
              ...((ex.attributes as Record<string, unknown> | undefined) ?? {}),
              [ORDER_ATTR]: i,
            },
          });
        }
        await api.patchConcept(workspaceId, branchId, wpId, {
          attributes: { ...createdAttrs, [ORDER_ATTR]: existingOrderedIds.length },
        });
      }
      await refreshAndActivate(lane.id);
      if (isNewWp) selectCard(wpId, `${lane.id}:${wpId}`, true, plateauId);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Drop op een Werkpakket-kaartje → Deliverable: WP→Deliverable + Deliverable→Program.
  const onDropOnWp = async (
    wpId: string,
    laneBranchId: string,
    e: React.DragEvent,
  ): Promise<void> => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPlateau(null);
    if (!workspaceId || !branchId || busy) return;
    if (useSession.getState().readOnly) return;
    // Activeer de plateau-branch van deze lane bij ÉLKE drop op een WP-kaartje.
    void switchTo(laneBranchId);
    const bulkRaw = e.dataTransfer.getData(EXISTING_CONCEPT_IDS_MIME) || null;
    const existingId = e.dataTransfer.getData(EXISTING_CONCEPT_MIME) || null;
    const typeId = e.dataTransfer.getData(TYPE_ID_MIME) || null;
    const droppedName = readDroppedTypeName(e.dataTransfer);
    const store = useWorkspaceData.getState().store;
    // Multi-select drag → elke Deliverable in de selectie aan deze WP koppelen
    // (kopiëren, dup-checked; geen verplaats/kopieer-vraag bij bulk).
    if (bulkRaw) {
      const ids = parseBulkIds(bulkRaw);
      const delIds = ids.filter((id) => store.getConcept(id)?.typeId === DELIVERABLE_TYPE);
      if (delIds.length === 0) {
        setNotice(t("roadmap.dropDeliverableOnWp"));
        return;
      }
      setBusy(true);
      try {
        const programId = await ensureProgram();
        for (const delId of delIds) {
          await ensureRealization(wpId, delId);
          if (programId) await ensureRealization(delId, programId);
        }
        await refreshAndActivate(laneBranchId);
      } catch (err) {
        window.alert(err instanceof ApiError ? err.message : String(err));
      } finally {
        setBusy(false);
      }
      return;
    }
    const draggedType =
      typeId ?? (existingId ? (store.getConcept(existingId)?.typeId ?? null) : null);
    // #4 — WP op een ander WP-kaartje in dezelfde lane → herschikken. De
    // gesleepte WP landt net vóór de doel-WP; alle WP's in de lane worden
    // hernummerd. Deliverables hangen genest onder hun WP, dus die verhuizen
    // automatisch mee (structureByPlateau).
    if (draggedType === WP_TYPE && existingId && existingId !== wpId) {
      const laneWps = (structureByPlateau.get(laneBranchId) ?? []).map((x) => x.wp.id);
      const fromIdx = laneWps.indexOf(existingId);
      if (fromIdx !== -1 && laneWps.includes(wpId)) {
        const reordered = laneWps.filter((id) => id !== existingId);
        reordered.splice(reordered.indexOf(wpId), 0, existingId);
        setBusy(true);
        try {
          await persistWpOrder(reordered);
          await refreshAndActivate(laneBranchId);
        } catch (err) {
          window.alert(err instanceof ApiError ? err.message : String(err));
        } finally {
          setBusy(false);
        }
      }
      return;
    }
    if (draggedType !== DELIVERABLE_TYPE) {
      // Bestaand niet-deliverable concept (bv. WP uit een andere lane) →
      // stil negeren; alleen bij een palette-drop van een fout type melden.
      if (!existingId) setNotice(t("roadmap.dropDeliverableOnWp"));
      return;
    }
    // Verplaats/kopieer-vraag: hoort deze BESTAANDE deliverable al bij een
    // ander werkpakket? Dan een 3-keuze-modal (verplaatsen / kopiëren /
    // annuleren) i.p.v. direct uitvoeren.
    if (existingId && !hasRealization(wpId, existingId)) {
      const otherOwners = store
        .listRelations()
        .filter(
          (r) =>
            !r.tombstoneInBranch &&
            r.typeId === REL_TYPE &&
            r.targetConceptId === existingId &&
            r.sourceConceptId !== wpId &&
            store.getConcept(r.sourceConceptId)?.typeId === WP_TYPE,
        );
      if (otherOwners.length > 0) {
        setPendingMove({
          wpId,
          delId: existingId,
          ownerRelIds: otherOwners.map((r) => r.id),
          laneBranchId,
        });
        return;
      }
    }
    setBusy(true);
    try {
      const programId = await ensureProgram();
      let delId = existingId;
      if (!delId && typeId) {
        const created = await api.createConcept(workspaceId, branchId, {
          type_id: typeId,
          ...(droppedName ? { name: droppedName } : {}),
        });
        delId = (created as { id: string }).id;
      }
      if (!delId) return;
      await ensureRealization(wpId, delId);
      if (programId) await ensureRealization(delId, programId);
      await refreshAndActivate(laneBranchId);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  // Voltooit een verplaats/kopieer-keuze uit de modal. `removeOwnerRelIds`
  // gevuld = verplaatsen (oude WP→deliverable-koppeling(en) weg); leeg = kopiëren.
  const finishWpDelivery = async (
    wpId: string,
    delId: string,
    removeOwnerRelIds: string[],
    laneBranchId: string,
  ): Promise<void> => {
    if (!workspaceId || !branchId) return;
    if (useSession.getState().readOnly) return;
    setPendingMove(null);
    setBusy(true);
    try {
      const programId = await ensureProgram();
      await ensureRealization(wpId, delId);
      if (programId) await ensureRealization(delId, programId);
      for (const rid of removeOwnerRelIds) {
        await api.deleteRelation(workspaceId, branchId, rid);
      }
      await refreshAndActivate(laneBranchId);
    } catch (err) {
      window.alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative flex h-full w-full flex-col overflow-hidden bg-white">
      {mergeLane && workspaceId && (
        <MergeBranchDialog
          workspaceId={workspaceId}
          branch={{ id: mergeLane.id, name: laneLabel(mergeLane) }}
          onClose={() => setMergeLane(null)}
          onMerged={(res) => {
            setMergeLane(null);
            // Ctrl+Z van vóór de merge zou op de oude toestand afspelen.
            useWorkspaceData.setState({ userUndoStack: [], userRedoStack: [] });
            void (async () => {
              if (branchId === res.source.id) await switchTo(res.target.id);
              else if (branchId) await useWorkspaceData.getState().hydrate(workspaceId, branchId);
              await refresh();
            })();
          }}
        />
      )}
      <div className="border-slate-200 border-b px-4 py-2">
        <h2 className="text-center font-semibold text-slate-700 text-sm">{t("roadmap.title")}</h2>
        <p className="mt-0.5 text-center text-slate-500 text-xs">{t("roadmap.subtitle")}</p>
      </div>

      {state === "loading" && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-slate-500 text-sm">
          <Spinner />
          {t("roadmap.loading")}
        </div>
      )}
      {state === "error" && <p className="p-4 text-red-600 text-sm">{t("roadmap.error")}</p>}

      {/* Blokkerende spinner tijdens plateau-aanmaak (concept-insert + branch +
          volledige re-hydrate — in productie trager dan lokaal). */}
      <ImportingModal visible={busy} messageNl="Plateau aanmaken…" messageEn="Creating plateau…" />

      {selectedBoxKey && !readOnly && (
        <BoxFormatToolbar
          style={boxStyles[selectedBoxKey] ?? {}}
          onChange={(p) => patchBox(selectedBoxKey, p)}
          onClose={() => setSelectedBoxKey(null)}
        />
      )}

      {state === "ready" && (
        <>
          {/* Lanes */}
          <ZoomPan className="min-h-0 flex-1" persistKey={diagram.id}>
            <div className="flex gap-3 p-4">
              {/* Zij-knoppen: open het Gaps- en Differences-diagram */}
              <div className="flex w-10 shrink-0 flex-col gap-3 py-0.5">
                {(
                  [
                    { id: siblingDiagrams.gap, label: t("roadmap.gaps") },
                    { id: siblingDiagrams.diff, label: t("roadmap.differences") },
                  ] as const
                ).map((b) => (
                  <button
                    key={b.label}
                    type="button"
                    disabled={!b.id}
                    onClick={() => openSibling(b.id)}
                    title={b.id ? b.label : undefined}
                    className="flex flex-1 items-center justify-center rounded-lg border border-orange-300 text-orange-700 text-sm hover:bg-orange-50 disabled:opacity-40"
                    style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <div
                className="grid shrink-0 gap-3"
                style={{ gridTemplateColumns: `repeat(${Math.max(layout.cols, 1)}, 16rem)` }}
              >
                {layout.cells.map(({ lane, idx, col, row }) => {
                  const isActive = lane.id === branchId;
                  const isNow = idx === 0;
                  const color = laneColors.get(lane.id) ?? null;
                  return (
                    <div
                      key={lane.id}
                      className={`flex w-64 shrink-0 flex-col rounded-lg border ${
                        isActive ? "ring-2 ring-blue-400" : ""
                      } ${color ? "" : isActive ? "border-blue-400" : "border-slate-300"}`}
                      style={{
                        gridColumn: col + 1,
                        gridRow: row + 1,
                        ...(color ? { borderColor: color.border } : {}),
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => void switchTo(lane.id)}
                        title={t("roadmap.switchHint")}
                        className={`flex items-center justify-between gap-2 rounded-t-lg px-3 py-2 text-left ${
                          color ? "" : isActive ? "bg-blue-50" : "bg-slate-50 hover:bg-slate-100"
                        }`}
                        style={
                          color
                            ? {
                                backgroundColor: color.bg,
                                // Subtiele donkere overlay → kop iets donkerder dan
                                // de body, zodat 't verschil zichtbaar is.
                                backgroundImage:
                                  "linear-gradient(rgba(0,0,0,0.09), rgba(0,0,0,0.09))",
                                color: color.text,
                              }
                            : undefined
                        }
                      >
                        <span className="flex items-center gap-2">
                          <span
                            className="font-medium text-slate-800 text-sm"
                            style={color ? { color: color.text } : undefined}
                          >
                            {laneLabel(lane)}
                          </span>
                          <AiBadge client={lane.createdViaClient} />
                          {isNow && (
                            <span className="rounded-full bg-emerald-100 px-1.5 py-0.5 text-[10px] text-emerald-700">
                              {t("roadmap.now")}
                            </span>
                          )}
                        </span>
                        {isActive && (
                          <span className="rounded-full bg-blue-500 px-1.5 py-0.5 text-[10px] text-white">
                            {t("roadmap.active")}
                          </span>
                        )}
                      </button>
                      {!lane.isMain &&
                        !readOnly &&
                        lane.parentBranchId &&
                        (() => {
                          const parent = lanes.find((l) => l.id === lane.parentBranchId);
                          if (!parent) return null;
                          return (
                            <button
                              type="button"
                              onClick={() => setMergeLane(lane)}
                              title={t("roadmap.mergeHint")}
                              className="border-slate-200 border-b bg-white/60 px-3 py-0.5 text-left text-[11px] text-slate-600 hover:bg-white"
                            >
                              {t("roadmap.mergeIntoParent", { parent: laneLabel(parent) })}
                            </button>
                          );
                        })()}
                      {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel */}
                      <div
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = "copy";
                          if (lane.plateauConceptId) setDragOverPlateau(lane.plateauConceptId);
                        }}
                        onDragLeave={() => setDragOverPlateau(null)}
                        onDrop={(e) => void onDropOnLane(lane, e)}
                        className={`flex min-h-[80px] flex-1 flex-col gap-1 rounded-b-lg p-2 ${
                          dragOverPlateau && dragOverPlateau === lane.plateauConceptId
                            ? "ring-2 ring-blue-400 ring-inset"
                            : ""
                        }`}
                        style={color ? { backgroundColor: color.bg } : undefined}
                      >
                        {(() => {
                          const wps = structureByPlateau.get(lane.id) ?? [];
                          if (wps.length === 0) {
                            return (
                              <div className="flex flex-1 items-center justify-center px-2 text-center text-slate-400 text-xs">
                                {t("roadmap.dropHint")}
                              </div>
                            );
                          }
                          return wps.map(({ wp, deliverables }) => (
                            <div key={wp.id} className="flex flex-col gap-1">
                              {/* Werkpakket — links uitgelijnd, accepteert Deliverable-drops */}
                              <ObjectCard
                                concept={wp}
                                align="left"
                                lang={lang}
                                t={t}
                                isSelected={
                                  selectedConceptId === wp.id &&
                                  selectedCard?.cardKey === `${lane.id}:${wp.id}`
                                }
                                boxStyle={boxStyles[`roadmap:${wp.id}`]}
                                onSelect={() => {
                                  selectCard(
                                    wp.id,
                                    `${lane.id}:${wp.id}`,
                                    true,
                                    lane.plateauConceptId ?? null,
                                  );
                                  setSelectedBoxKey(`roadmap:${wp.id}`);
                                }}
                                onContextMenu={(e) => {
                                  if (readOnly) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setCtxMenu({
                                    x: e.clientX,
                                    y: e.clientY,
                                    conceptId: wp.id,
                                    isWp: true,
                                    parentId: lane.plateauConceptId ?? null,
                                  });
                                }}
                                onDropDeliverable={(e) => void onDropOnWp(wp.id, lane.id, e)}
                              />
                              {/* Deliverables — rechts uitgelijnd onder de WP */}
                              {deliverables.map((d) => (
                                <ObjectCard
                                  key={d.id}
                                  concept={d}
                                  align="right"
                                  lang={lang}
                                  t={t}
                                  isSelected={
                                    selectedConceptId === d.id &&
                                    selectedCard?.cardKey === `${lane.id}:${wp.id}:${d.id}`
                                  }
                                  boxStyle={boxStyles[`roadmap:${d.id}`]}
                                  onSelect={() => {
                                    selectCard(d.id, `${lane.id}:${wp.id}:${d.id}`, false, wp.id);
                                    setSelectedBoxKey(`roadmap:${d.id}`);
                                  }}
                                  onContextMenu={(e) => {
                                    if (readOnly) return;
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setCtxMenu({
                                      x: e.clientX,
                                      y: e.clientY,
                                      conceptId: d.id,
                                      isWp: false,
                                      parentId: wp.id,
                                    });
                                  }}
                                />
                              ))}
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* "+"-lane: nieuw plateau */}
              <div className="flex w-64 shrink-0 flex-col">
                {adding ? (
                  <div className="flex flex-col gap-2 rounded-lg border border-slate-300 border-dashed p-3">
                    <input
                      type="text"
                      // biome-ignore lint/a11y/noAutofocus: bewuste focus-op-openen zodat de gebruiker meteen kan typen
                      autoFocus
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") void addPlateau();
                        if (e.key === "Escape") {
                          setAdding(false);
                          setNewName("");
                        }
                      }}
                      placeholder={t("roadmap.plateauNamePlaceholder")}
                      className="rounded border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        disabled={busy || !newName.trim()}
                        onClick={() => void addPlateau()}
                        className="rounded bg-blue-500 px-2 py-1 text-white text-xs hover:bg-blue-600 disabled:opacity-50"
                      >
                        {t("roadmap.addConfirm")}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setAdding(false);
                          setNewName("");
                        }}
                        className="rounded px-2 py-1 text-slate-500 text-xs hover:bg-slate-100"
                      >
                        {t("roadmap.cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setAdding(true)}
                    hidden={readOnly}
                    title={t("roadmap.addPlateau")}
                    className="flex h-20 items-center justify-center rounded-lg border border-slate-300 border-dashed text-slate-400 text-sm hover:border-blue-400 hover:text-blue-500"
                  >
                    + {t("roadmap.addPlateau")}
                  </button>
                )}
              </div>
            </div>

            {/* Tijd-slider = branch-switch. De balk is exact zo lang als er
              plateaus zijn (één w-64-cel per plateau, gelijk aan de lanes
              hierboven); de rechthoekige marker staat gecentreerd onder het
              plateau dat via de actieve branch focus heeft. */}
            {lanes.length > 1 && (
              <div className="border-slate-200 border-t p-4">
                <div className="mb-2 text-slate-500 text-xs">{t("roadmap.timeSlider")}</div>
                <div className="overflow-x-auto">
                  <div className="relative flex w-max gap-3">
                    {/* track-lijn over de volle plateau-breedte */}
                    <div className="pointer-events-none absolute inset-x-0 top-3 h-0.5 -translate-y-1/2 rounded bg-slate-200" />
                    {/* Spacer die de w-10 zij-as-kolom (Gaps/Differences) van de
                      lanes-rij hierboven matcht, zodat de slider-cellen exact
                      onder hun plateau-kolom uitlijnen. */}
                    <div aria-hidden className="w-10 shrink-0" />
                    {/* Eén stap per tijdkolom; parallelle plateaus staan als
                      namen onder elkaar. */}
                    {columns.map((col) => (
                      <div
                        key={`slider-${col[0]?.lane.id}`}
                        className="relative z-10 flex w-64 shrink-0 flex-col items-center gap-1"
                      >
                        <span className="flex h-6 items-center justify-center">
                          {col.some(({ idx }) => idx === activeIndex) && (
                            <span className="h-4 w-8 rounded-sm bg-blue-500 shadow-sm" />
                          )}
                        </span>
                        {col.map(({ lane, idx }) => (
                          <button
                            key={lane.id}
                            type="button"
                            onClick={() => void switchTo(lane.id)}
                            title={t("roadmap.switchHint")}
                            className={`text-[10px] ${idx === activeIndex ? "font-semibold text-blue-600" : "text-slate-400 hover:text-slate-600"}`}
                          >
                            {laneLabel(lane)}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </ZoomPan>
        </>
      )}

      {pendingMove && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30">
          <div className="w-[340px] rounded-lg border border-slate-200 bg-white p-4 shadow-xl">
            <div className="mb-3 text-slate-700 text-sm">{t("roadmap.moveOrCopyDeliverable")}</div>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-slate-600 text-sm hover:bg-slate-50"
                onClick={() => setPendingMove(null)}
              >
                {t("roadmap.cancel")}
              </button>
              <button
                type="button"
                className="rounded border border-slate-300 px-3 py-1.5 text-slate-700 text-sm hover:bg-slate-50"
                onClick={() =>
                  void finishWpDelivery(
                    pendingMove.wpId,
                    pendingMove.delId,
                    [],
                    pendingMove.laneBranchId,
                  )
                }
              >
                {t("roadmap.copy")}
              </button>
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                onClick={() =>
                  void finishWpDelivery(
                    pendingMove.wpId,
                    pendingMove.delId,
                    pendingMove.ownerRelIds,
                    pendingMove.laneBranchId,
                  )
                }
              >
                {t("roadmap.move")}
              </button>
            </div>
          </div>
        </div>
      )}

      {notice && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30">
          <div className="w-[340px] rounded-lg border border-slate-200 bg-white p-4 shadow-xl">
            <div className="mb-3 text-slate-700 text-sm">{notice}</div>
            <div className="flex justify-end">
              <button
                type="button"
                className="rounded bg-blue-600 px-3 py-1.5 text-sm text-white hover:bg-blue-700"
                onClick={() => setNotice(null)}
              >
                {t("roadmap.ok")}
              </button>
            </div>
          </div>
        </div>
      )}

      {ctxMenu && (
        <>
          {/* Transparante backdrop — klik buiten sluit het menu. */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: eigen canvas-cel; keyboard-a11y hoort op canvas-grid-niveau (nog te doen), niet per cel */}
          <div className="fixed inset-0 z-40" onClick={() => setCtxMenu(null)} />
          <div
            className="fixed z-50 min-w-[180px] rounded-md border border-slate-200 bg-white py-1 shadow-lg"
            style={{ left: ctxMenu.x, top: ctxMenu.y }}
          >
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-slate-700 text-sm hover:bg-slate-100"
              onClick={() =>
                void performDelete(ctxMenu.conceptId, ctxMenu.isWp, false, ctxMenu.parentId)
              }
            >
              {t("roadmap.removeFromRoadmap")}
            </button>
            <button
              type="button"
              className="block w-full px-3 py-1.5 text-left text-red-600 text-sm hover:bg-red-50"
              onClick={() =>
                void performDelete(ctxMenu.conceptId, ctxMenu.isWp, true, ctxMenu.parentId)
              }
            >
              {t("roadmap.hardDelete")}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
